import React, { useState, useEffect } from 'react';
import EmailNameForm from './EmailNameForm.js';
import { validateNameEmail } from './emailSignup.js';

// Hero-styled "Join Waitlist for Beta Launch" CTA. Replaces the paused "Get Started"
// button on Home (and Pricing) during the Vicktoria rework (2026-06-12).
//
// Flow: a single hero-styled button is shown in the spot Get Started used to
// occupy. On click ("on selection") it reveals a name + email form (shared
// EmailNameForm). BOTH are required so launch-announcement emails can be
// personalized. Submits to POST /api/waitlist/join (prod NGINX strips /api ->
// Express /waitlist/join), which stores the entry in MongoDB `waitlist_entries`.
//
// Styling intentionally mirrors the old Home hero Get Started button so the
// page looks unchanged except for function.
const HERO_BUTTON_STYLE = {
  backgroundColor: '#4A8EB3',
  color: '#FFFFFF',
  border: 'none',
  padding: '18px 48px',
  borderRadius: '15px',
  fontSize: '24px',
  cursor: 'pointer',
  fontFamily: 'Judson, serif',
  fontWeight: '600',
};

const WaitlistButton = ({ loggedInUser }) => {
  const [showForm, setShowForm] = useState(false);
  const [hasJoined, setHasJoined] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Prefill from the logged-in account when available. The form still shows
  // for everyone (uniform UX during the pause) — these are just defaults.
  useEffect(() => {
    if (loggedInUser) {
      if (loggedInUser.name) setName(loggedInUser.name);
      if (loggedInUser.email) setEmail(loggedInUser.email);
    }
  }, [loggedInUser]);

  // Respect the 24h "already joined" cookie so refreshers don't re-prompt.
  useEffect(() => {
    if (document.cookie.split(';').some((item) => item.trim().startsWith('waitListSticker='))) {
      setHasJoined(true);
    }
  }, [loggedInUser]);

  const handleSubmit = async () => {
    if (submitting) return;

    const valid = validateNameEmail(name, email);
    if (!valid.ok) {
      setError(valid.error);
      return;
    }

    setError("");
    setSubmitting(true);

    try {
      const response = await fetch('/api/waitlist/join', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          identifier: valid.email,
          name: valid.name,
          userType: loggedInUser ? "user" : "guest",
        }),
      });

      if (response.ok) {
        const date = new Date();
        date.setTime(date.getTime() + (24 * 60 * 60 * 1000));
        document.cookie = `waitListSticker=true; expires=${date.toUTCString()}; path=/`;
        setHasJoined(true);
        setShowForm(false);
      } else {
        const data = await response.json().catch(() => ({}));
        setError(data.error || "Failed to join the waitlist.");
      }
    } catch (err) {
      console.error("Waitlist error:", err);
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Already joined — confirmation in the same spot.
  if (hasJoined) {
    return (
      <button
        type="button"
        disabled
        style={{ ...HERO_BUTTON_STYLE, backgroundColor: '#888', cursor: 'default' }}
      >
        You're on the list! ✓
      </button>
    );
  }

  // Initial CTA — occupies the spot where Get Started used to be.
  if (!showForm) {
    return (
      <button type="button" style={HERO_BUTTON_STYLE} onClick={() => setShowForm(true)}>
        Join Waitlist for Beta Launch
      </button>
    );
  }

  // Revealed form: name + email, both required (shared component).
  return (
    <EmailNameForm
      name={name}
      email={email}
      onNameChange={setName}
      onEmailChange={setEmail}
      error={error}
      submitting={submitting}
      submitLabel="Join"
      submittingLabel="Joining…"
      submitStyle={HERO_BUTTON_STYLE}
      onSubmit={handleSubmit}
    />
  );
};

export default WaitlistButton;
